import React from 'react';
import { Link } from 'react-router-dom';
import { FaHome, FaChevronRight, FaArrowRight } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const ITServicesPage = () => {
    const services = [
        {
            title: 'Data Center Construction & Commissioning',
            description: 'End-to-end construction and commissioning management for mission-critical data center projects, from L1–L5 commissioning through IST, RFS, and TCO support.',
            image: '/serviceImages/dc-construction/dc-commissioning-hero.jpeg',
            gradient: 'from-blue-700 to-cyan-500',
            link: '/services/data-center-commissioning'
        },
        {
            title: 'Data Center Security',
            description: 'End-to-end security solutions combining advanced system installations with trained security personnel — CCTV, access control, on-site guards, and 24/7 SOC monitoring.',
            image: '/serviceImages/dc-security/security-hero.jpg',
            gradient: 'from-slate-700 to-blue-700',
            link: '/services/data-center-security'
        },
        {
            title: 'Safety Professionals',
            description: 'Expert safety professionals for mission-critical data center operations, construction, and maintenance — ensuring OSHA compliance, zero incidents, and full regulatory readiness.',
            image: '/serviceImages/safety-professionals.jpg',
            gradient: 'from-yellow-500 to-amber-600',
            link: '/services/safety-professionals'
        },
        {
            title: 'PPE Solutions',
            description: 'Complete Personal Protective Equipment solutions for data center safety and compliance.',
            image: '/serviceImages/ppe-solutions.svg',
            gradient: 'from-yellow-600 to-amber-500',
            link: '/services/ppe-solutions'
        },
        /* {
            title: 'Janitorial Staffing',
            description: 'Trained and dependable janitorial resources specialized in maintaining clean, secure, and high-performance data center environments with anti-static protocols.',
            image: '/serviceImages/dc-janitorial/janitorial-hero.jpg',
            gradient: 'from-teal-600 to-cyan-500',
            link: '/services/janitorial-staffing'
        }, */
        {
            title: '24/7 IT Support - On-site Technicians',
            description: 'Our team of expert technicians is available around the clock to provide immediate on-site support for all your IT needs.',
            image: '/serviceImages/onsite-technicians.png',
            gradient: 'from-blue-600 to-cyan-500',
            link: '/services/it-support'
        },
        {
            title: 'IT Deployment',
            description: 'Comprehensive IT deployment services including workstation setup, software installation, and system configuration.',
            image: '/serviceImages/it-deployment.png',
            gradient: 'from-cyan-500 to-teal-500',
            link: '/services/it-deployment'
        },
        {
            title: 'Network Infrastructure & Cabling',
            description: 'Professional structured cabling and network infrastructure solutions designed for scalability and peak performance.',
            image: '/serviceImages/network-cabling.png',
            gradient: 'from-indigo-600 to-blue-500',
            link: '/services/infrastructure'
        },
        {
            title: 'IT Support 24/7 - Help Desk',
            description: 'Round-the-clock remote technical support and help desk services for immediate issue resolution.',
            image: '/serviceImages/help-desk.png',
            gradient: 'from-purple-600 to-indigo-500',
            link: '/services/it-support'
        },
        {
            title: 'IT Parts Logistics',
            description: 'Efficient spare parts management and logistics to ensure minimal downtime for your operations.',
            image: '/serviceImages/parts-logistics.png',
            gradient: 'from-orange-500 to-red-500',
            link: '/services/hardware-deployment'
        },
        {
            title: 'Preventive Maintenance',
            description: 'Scheduled maintenance programs to prevent issues before they occur and extend equipment lifespan.',
            image: '/serviceImages/preventive-maintenance.png',
            gradient: 'from-teal-500 to-green-500',
            link: '/services/preventive-maintenance'
        },
        {
            title: 'IoT Solutions',
            description: 'Cutting-edge Internet of Things solutions for smart connectivity and automation across your business.',
            image: '/serviceImages/iot-solutions.png',
            gradient: 'from-violet-600 to-purple-500',
            link: '/services/iot'
        },
        {
            title: 'Break and Fix',
            description: 'Rapid response repair services for all types of IT equipment with quick turnaround times.',
            image: '/serviceImages/break-fix.png',
            gradient: 'from-rose-500 to-pink-500',
            link: '/services/break-fix'
        },
    ];

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 py-24 overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>

                {/* Gradient Orbs */}
                <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    {/* Breadcrumb */}
                    <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                        <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                            <FaHome size={14} />
                            <span>Home</span>
                        </Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <span className="text-cyan-400">IT Services</span>
                    </nav>

                    <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                        IT <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Services</span>
                    </h1>
                    <p className="text-xl text-gray-300 max-w-3xl">
                        Comprehensive IT solutions for your business needs across North America
                    </p>
                </div>
            </section>

            {/* Introduction Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="inline-block mb-4 px-4 py-2 bg-gradient-to-r from-blue-100 to-cyan-100 border border-blue-200 rounded-full">
                                <span className="text-blue-600 text-sm font-bold tracking-wider">115+ SERVICE POINTS</span>
                            </div>
                            <h2 className="text-4xl font-bold text-gray-900 mb-6">
                                IT Solutions for All Your Branches Across North America
                            </h2>
                            <p className="text-gray-600 leading-relaxed mb-6">
                                Covering more than 115 service points in North America, our team of technicians is made up of IT experts with extensive experience in troubleshooting all kinds of problems, including hardware failures, software errors, and network connectivity issues, as well as repairing, replacing, adding, and installing computer equipment.
                            </p>
                            <p className="text-gray-600 leading-relaxed">
                                With our incident management system, our team of technicians and service coordinators informs you in real time of the resolution of the incident. Our on-site interventions meet your expectations. Our technicians can be on site on short notice to meet your deadlines.
                            </p>
                        </div>
                        <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-3xl transform rotate-3 opacity-20"></div>
                            <img
                                src="/serviceImages/onsite-technicians.png"
                                alt="IT Services"
                                className="relative rounded-3xl shadow-2xl w-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Grid Section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
                {/* Background decorations */}
                <div className="absolute top-0 left-0 w-72 h-72 bg-blue-100 rounded-full filter blur-3xl opacity-30 -translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-100 rounded-full filter blur-3xl opacity-30 translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-7xl mx-auto relative z-10">
                    {/* Header */}
                    <div className="text-center mb-16">
                        <div className="inline-block mb-4 px-4 py-2 bg-gradient-to-r from-blue-100 to-cyan-100 border border-blue-200 rounded-full">
                            <span className="text-blue-600 text-sm font-bold tracking-wider">DISCOVER OUR SERVICES</span>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                            Comprehensive IT Solutions
                        </h2>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Comprehensive IT solutions for your business needs
                        </p>
                    </div>

                    {/* Services Grid */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {services.map((service, index) => (
                            <div
                                key={index}
                                className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 flex flex-col"
                            >
                                {/* Image */}
                                <div className="relative h-48 overflow-hidden flex-shrink-0">
                                    <img
                                        src={service.image}
                                        alt={service.title}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                    />
                                    <div className={`absolute inset-0 bg-gradient-to-t ${service.gradient} opacity-0 group-hover:opacity-60 transition-opacity duration-300`}></div>
                                </div>

                                {/* Content */}
                                <div className="p-6 flex flex-col flex-grow">
                                    <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                                        {service.title}
                                    </h3>
                                    <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                                        {service.description}
                                    </p>
                                    <div className="mt-auto">
                                        <Link
                                            to={service.link}
                                            className="inline-flex items-center gap-2 text-blue-600 font-semibold text-sm group-hover:text-cyan-600 transition-colors"
                                        >
                                            Learn More
                                            <FaArrowRight className="group-hover:translate-x-2 transition-transform" size={12} />
                                        </Link>
                                    </div>
                                </div>

                                {/* Bottom gradient line */}
                                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${service.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-500">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                        Ready to Transform Your IT Infrastructure?
                    </h2>
                    <p className="text-xl text-blue-100 mb-8">
                        Contact us today to discuss your IT needs and get a customized solution for your business.
                    </p>
                    <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-600 rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-white/30 transition-all duration-300 hover:-translate-y-1"
                    >
                        Get a Free Quote
                        <FaArrowRight />
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ITServicesPage;
